/** Auth and identity endpoints. */
import { api, setAccessToken } from './client';
import {
  authConfigSchema,
  meSchema,
  type AuthConfig,
  type AuthTokenResponse,
  type Me,
} from './types';

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const response = await api.get<unknown>('/auth/config', { skipAuthRetry: true });
  return authConfigSchema.parse(response.data);
}

export async function exchangeCode(input: {
  code: string;
  code_verifier: string;
  nonce: string;
  redirect_uri: string;
}): Promise<AuthTokenResponse> {
  const response = await api.post<AuthTokenResponse>('/auth/session', input, {
    skipAuthRetry: true,
  });
  setAccessToken(response.data.access_token, response.data.expires_in);
  return response.data;
}

/**
 * Revoke Build's session. `logout_url`, when present, signs the person out of
 * KorOS too (OIDC RP-initiated logout); null means a local sign-out only.
 */
export async function logoutRequest(): Promise<{ logout_url: string | null }> {
  const response = await api.post<{ logout_url?: string | null }>(
    '/auth/logout',
    {},
    { skipAuthRetry: true },
  );
  return { logout_url: response.data?.logout_url ?? null };
}

/** After KorOS sends the browser back: does `state` match the one set at sign-out? */
export async function verifySignedOutState(state: string): Promise<boolean> {
  const response = await api.post<{ state_valid?: boolean }>(
    '/auth/signed-out',
    { state },
    { skipAuthRetry: true },
  );
  return response.data?.state_valid === true;
}

/** An admin sign-in's state and nonce (bound to this browser by a signed cookie), and the
 * `acr_values` to ask KorOS for (two-step sign-in; empty = none). */
export type AdminFlow = { state: string; nonce: string; acr_values?: string };

/** Django admin sign-in: a state and nonce bound to this browser (signed cookie). */
export async function startDjangoAdminFlow(): Promise<AdminFlow> {
  const response = await api.post<AdminFlow>(
    '/auth/django-admin-start',
    {},
    { skipAuthRetry: true },
  );
  return response.data;
}

/** Back-office sign-in: its own state and nonce (its own signed cookie). */
export async function startBackofficeFlow(): Promise<AdminFlow> {
  const response = await api.post<AdminFlow>('/auth/backoffice-start', {}, { skipAuthRetry: true });
  return response.data;
}

/**
 * Exchange the code of a fresh, two-step KorOS sign-in for a back-office session
 * (a Django session cookie; no Build session). Answers the /backoffice/ path to load.
 */
export async function createBackofficeSession(input: {
  code: string;
  code_verifier: string;
  nonce: string;
  state: string;
  redirect_uri: string;
  next: string;
}): Promise<{ next: string }> {
  const response = await api.post<{ next: string }>('/auth/backoffice-session', input, {
    skipAuthRetry: true,
  });
  return response.data;
}

/** Development only: the back office as a seeded staff member (404 unless enabled). */
export async function devBackofficeSession(email: string, next: string): Promise<{ next: string }> {
  const response = await api.post<{ next: string }>(
    '/auth/dev-backoffice-session',
    { email, next },
    { skipAuthRetry: true },
  );
  return response.data;
}

/**
 * Exchange the code of a fresh KorOS sign-in for a Django admin session (a
 * Django session cookie; no Build session). Answers the /django-admin/ path to load.
 */
export async function createDjangoAdminSession(input: {
  code: string;
  code_verifier: string;
  nonce: string;
  state: string;
  redirect_uri: string;
  next: string;
}): Promise<{ next: string }> {
  const response = await api.post<{ next: string }>('/auth/django-admin-session', input, {
    skipAuthRetry: true,
  });
  return response.data;
}

/** Development only: Django admin as a seeded superuser (404 unless enabled). */
export async function devDjangoAdminSession(
  email: string,
  next: string,
): Promise<{ next: string }> {
  const response = await api.post<{ next: string }>(
    '/auth/dev-django-admin-session',
    { email, next },
    { skipAuthRetry: true },
  );
  return response.data;
}

export async function fetchMe(): Promise<Me> {
  const response = await api.get<unknown>('/me');
  return meSchema.parse(response.data);
}
